import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useMessengerContext } from '../hooks/useMessengerContext';
import { useCommunitiesContext } from '../hooks/useCommunitiesContext';
import './LoggedInHome.css';

const LoggedInHome = () => {
  console.log('[HOMEPAGE MESSAGE CHECK] 🏠 LoggedInHome component rendered');
  const { user } = useAuthContext();
  const { state: messengerState } = useMessengerContext();
  const { communities } = useCommunitiesContext();
  const [userCommunities, setUserCommunities] = useState([]);
  const [suggestedCommunities, setSuggestedCommunities] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentConversations, setRecentConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  console.log('[HOMEPAGE MESSAGE CHECK] 📊 LoggedInHome state:', { 
    hasUser: !!user, 
    userId: user?.userId,
    unreadCount, 
    conversationsCount: messengerState.conversations?.length || 0,
    loading,
    conversationsData: messengerState.conversations?.map(conv => ({
      id: conv._id,
      unReadMessages: conv.unReadMessages,
      lastMessage: conv.lastMessage?.text?.substring(0, 30),
      communityName: conv.communityName
    }))
  });

  // Get user's communities
  useEffect(() => {
    console.log('[HOMEPAGE MESSAGE CHECK] 🏠 LoggedInHome: Processing user communities', { 
      hasUser: !!user, 
      userCommunities: user?.listOfCommunities?.length || 0,
      totalCommunities: communities.length 
    });
    if (user?.listOfCommunities && communities.length > 0) {
      const userCommunitiesData = communities.filter(community => 
        user.listOfCommunities.includes(community._id)
      );
      setUserCommunities(userCommunitiesData);
      
      // Get a mix of suggested communities (both joined and not joined) for discovery
      const suggested = communities
        .slice(0, 6); // Show first 6 communities regardless of membership
      setSuggestedCommunities(suggested);
      
      console.log('[HOMEPAGE MESSAGE CHECK] ✅ LoggedInHome: Communities loaded, setting loading to false');
      setLoading(false);
    }
  }, [user, communities]);

  // Force unread calculation check
  if (messengerState.conversations && messengerState.conversations.length > 0) {
    const totalUnreadFromCount = messengerState.conversations.reduce((total, conv) => {
      return total + (conv.unreadMessageCount || (conv.unReadMessages ? 1 : 0));
    }, 0);
    console.log('[HOMEPAGE MESSAGE CHECK] 🔍 FORCE CHECK - Current conversations:', {
      totalConversations: messengerState.conversations.length,
      unreadConversations: messengerState.conversations.filter(conv => conv.unReadMessages),
      calculatedTotalUnread: totalUnreadFromCount,
      stateUnreadCount: unreadCount,
      allConversationsDetail: messengerState.conversations.map(conv => ({
        id: conv._id,
        unReadMessages: conv.unReadMessages,
        unreadMessageCount: conv.unreadMessageCount,
        lastMessage: conv.lastMessage?.text?.substring(0, 50),
        communityName: conv.communityName
      }))
    });
  }

  // Calculate unread messages
  useEffect(() => {
    console.log('[HOMEPAGE MESSAGE CHECK] 🏠 LoggedInHome: Processing conversations', { 
      hasConversations: !!messengerState.conversations, 
      conversationCount: messengerState.conversations?.length || 0,
      conversations: messengerState.conversations 
    });
    if (messengerState.conversations) {
      // Calculate total unread messages across all conversations
      let totalUnreadMessages = 0;
      
      messengerState.conversations.forEach(conv => {
        // Use the unreadMessageCount from backend if available, otherwise fallback to 1 per conversation
        const unreadCount = conv.unreadMessageCount || (conv.unReadMessages ? 1 : 0);
        totalUnreadMessages += unreadCount;
      });
      
      console.log('[HOMEPAGE MESSAGE CHECK] 💬 Unread message calculation:', { 
        totalConversations: messengerState.conversations.length,
        unreadConversations: messengerState.conversations.filter(conv => conv.unReadMessages),
        totalUnreadMessages: totalUnreadMessages,
        allConversations: messengerState.conversations.map(conv => ({
          id: conv._id,
          unReadMessages: conv.unReadMessages,
          unreadMessageCount: conv.unreadMessageCount,
          lastMessage: conv.lastMessage?.text?.substring(0, 50),
          communityName: conv.communityName
        }))
      });
      setUnreadCount(totalUnreadMessages);
      
      // Get recent conversations (last 3)
      const recent = messengerState.conversations
        .filter(conv => conv.lastMessage)
        .sort((a, b) => new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt))
        .slice(0, 3);
      console.log('[HOMEPAGE MESSAGE CHECK] 🕰️ Recent conversations:', recent);
      setRecentConversations(recent);
    } else {
      console.log('[HOMEPAGE MESSAGE CHECK] ⚠️ No conversations found in messenger state');
    }
  }, [messengerState.conversations]);

  const handleCommunityClick = (community) => {
    window.location.href = `/${community.databaseRef}`;
  };

  const handleJoinCommunity = (community) => {
    window.location.href = `/JoinAdditionalCommunity/${community._id}`;
  };

  const handleConnectToMembers = (community) => {
    // Navigate to messenger and auto-select the community bot conversation with connect action
    window.location.href = `/messenger?communityId=${community._id}&action=connect`;
  };

  const isUserMember = (communityId) => {
    return user?.listOfCommunities?.includes(communityId);
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const messageDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - messageDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className="logged-in-home">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="logged-in-home">
      {/* Header Section */}
      <section className="welcome-section">
        <div className="welcome-container">
          <h1 className="welcome-title">
            Welcome back, {user?.name || 'Friend'}! 👋
          </h1>
          <p className="welcome-subtitle">
            Here's what's happening in your communities
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="quick-stats">
        <div className="stats-container">
          <div className="stat-card">
            <div className="stat-icon">🏘️</div>
            <div className="stat-content">
              <div className="stat-number">{userCommunities.length}</div>
              <div className="stat-label">Your Communities</div>
            </div>
          </div>
          <div className="stat-card messages-stat" onClick={() => window.location.href = '/messenger'}>
            <div className="stat-icon">💬</div>
            <div className="stat-content">
              <div className="stat-number">
                {unreadCount}
                {unreadCount > 0 && <span className="unread-badge"></span>}
              </div>
              <div className="stat-label">New Messages</div>
            </div>
          </div>
          <div className="stat-card" onClick={() => window.location.href = '/ProfileUpdate'}>
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <div className="stat-number">
                {user?.profileCompleteness || 'View'}
              </div>
              <div className="stat-label">Profile</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="main-content-grid">
        
        {/* Your Communities Section */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Your Communities</h2>
            <a href="/groupsearch" className="section-link">View All</a>
          </div>
          <div className="communities-grid">
            {userCommunities.length > 0 ? (
              userCommunities.map(community => (
                <div 
                  key={community._id} 
                  className="community-card"
                  onClick={() => handleCommunityClick(community)}
                >
                  <div className="community-image-container">
                    {community.pics && community.pics[0] ? (
                      <img 
                        src={community.pics[0]} 
                        alt={community.title}
                        className="community-image"
                      />
                    ) : (
                      <div className="community-placeholder">
                        <span className="community-emoji">🏘️</span>
                      </div>
                    )}
                  </div>
                  <div className="community-info">
                    <h3 className="community-name">{community.title}</h3>
                    <p className="community-description">
                      {community.description?.substring(0, 60)}...
                    </p>
                    <div className="community-tags">
                      {community.tags && typeof community.tags === 'string' && 
                        community.tags.split(',').slice(0, 2).map((tag, index) => (
                          <span key={index} className="community-tag">
                            {tag.trim()}
                          </span>
                        ))
                      }
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <p>You haven't joined any communities yet!</p>
                <a href="/groupsearch" className="cta-button">
                  Find Communities
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Recent Messages Section */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Recent Messages</h2>
            <a href="/messenger" className="section-link">Open Messenger</a>
          </div>
          <div className="messages-list">
            {recentConversations.length > 0 ? (
              recentConversations.map(conversation => (
                <div 
                  key={conversation._id} 
                  className="message-item"
                  onClick={() => window.location.href = '/messenger'}
                >
                  <div className="message-avatar">
                    {conversation.communityPic ? (
                      <img src={conversation.communityPic} alt="Community" />
                    ) : (
                      <div className="avatar-placeholder">💬</div>
                    )}
                  </div>
                  <div className="message-content">
                    <div className="message-header">
                      <span className="message-name">
                        {conversation.communityName || 'Community Chat'}
                      </span>
                      <span className="message-time">
                        {formatTimeAgo(conversation.lastMessage?.createdAt)}
                      </span>
                    </div>
                    <p className="message-preview">
                      {conversation.lastMessage?.text?.substring(0, 50)}...
                    </p>
                  </div>
                  {conversation.unReadMessages && (
                    <div className="unread-indicator"></div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <p>No recent messages</p>
                <a href="/messenger" className="cta-button">
                  Start Chatting
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Discover More Communities */}
        <section className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Discover More</h2>
            <a href="/groupsearch" className="section-link">Browse All</a>
          </div>
          <div className="suggestions-grid">
            {suggestedCommunities.map(community => {
              const isMember = isUserMember(community._id);
              return (
                <div key={community._id} className="suggestion-card">
                  <div className="suggestion-image-container">
                    {community.pics && community.pics[0] ? (
                      <img 
                        src={community.pics[0]} 
                        alt={community.title}
                        className="suggestion-image"
                      />
                    ) : (
                      <div className="suggestion-placeholder">
                        <span className="suggestion-emoji">✨</span>
                      </div>
                    )}
                  </div>
                  <div className="suggestion-content">
                    <h4 className="suggestion-name">{community.title}</h4>
                    <p className="suggestion-description">
                      {community.description?.substring(0, 40)}...
                    </p>
                    {isMember ? (
                      <>
                        <p className="member-status">
                          You are a member of this group
                        </p>
                        <button 
                          className="connect-button"
                          onClick={() => handleConnectToMembers(community)}
                        >
                          Connect with Members
                        </button>
                      </>
                    ) : (
                      <button 
                        className="join-button"
                        onClick={() => handleJoinCommunity(community)}
                      >
                        Join Community
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="dashboard-section quick-actions">
          <div className="section-header">
            <h2 className="section-title">Quick Actions</h2>
          </div>
          <div className="actions-grid">
            <a href="/ProfileUpdate" className="action-card">
              <div className="action-icon">✏️</div>
              <div className="action-content">
                <h3>Update Profile</h3>
                <p>Keep your information current</p>
              </div>
            </a>
            <a href="/groupsearch" className="action-card">
              <div className="action-icon">🔍</div>
              <div className="action-content">
                <h3>Find Communities</h3>
                <p>Discover new groups to join</p>
              </div>
            </a>
            <a href="/newcommunityentry" className="action-card">
              <div className="action-icon">➕</div>
              <div className="action-content">
                <h3>Create Community</h3>
                <p>Start your own group</p>
              </div>
            </a>
            <a href="/settings" className="action-card">
              <div className="action-icon">⚙️</div>
              <div className="action-content">
                <h3>Settings</h3>
                <p>Manage your preferences</p>
              </div>
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default LoggedInHome;